import React, { useState, useEffect } from 'react';
import { X, Loader2, Users } from 'lucide-react';
import { useChatScreen } from '@/stores/useChatScreen';

interface CreateGroupModalProps {
    isOpen: boolean;
    onClose: () => void;
    businessId?: string;
    propertyId?: string;
    context: 'personal' | 'business' | 'property';
}

const CreateGroupModal: React.FC<CreateGroupModalProps> = ({
    isOpen,
    onClose,
    businessId,
    propertyId,
    context
}) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
    
    const { 
        potentialMembers, 
        fetchPotentialMembers, 
        createGroup, 
        creatingGroup, 
        groupError,
        clearErrors
    } = useChatScreen();

    useEffect(() => {
        if (isOpen) {
            setName('');
            setDescription('');
            setSelectedMembers([]);
            clearErrors();
            fetchPotentialMembers(context, businessId, propertyId);
        }
    }, [isOpen, context, businessId, propertyId, fetchPotentialMembers, clearErrors]);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) return;

        try {
            await createGroup(
                name.trim(),
                selectedMembers,
                context,
                businessId,
                propertyId,
                description.trim() || undefined
            );
            onClose();
        } catch (error) {
            console.error('Failed to create group:', error);
        }
    };

    const toggleMember = (id: string) => {
        setSelectedMembers(prev => 
            prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]
        );
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="w-full max-w-md bg-white dark:bg-dark-card rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-[90vh]">
                <div className="flex items-center justify-between p-4 border-b border-light-border dark:border-dark-border">
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Create New Group</h2>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4">
                    <form id="create-group-form" onSubmit={handleSubmit} className="space-y-4">
                        {groupError && (
                            <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm rounded-lg">
                                {groupError}
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Group Name *
                            </label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-800 dark:text-white"
                                placeholder="e.g., Team Updates"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Description (Optional)
                            </label>
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-800 dark:text-white resize-none"
                                placeholder="What is this group for?"
                                rows={3}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Select Members ({selectedMembers.length} selected)
                            </label>
                            <div className="space-y-2 max-h-48 overflow-y-auto p-2 border border-light-border dark:border-dark-border rounded-lg bg-gray-50 dark:bg-gray-900/50">
                                {potentialMembers.length === 0 ? (
                                    <p className="text-sm text-center text-gray-500 py-4">No available members found.</p>
                                ) : (
                                    potentialMembers.map(member => (
                                        <div 
                                            key={member.id}
                                            onClick={() => toggleMember(member.id)}
                                            className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                                                selectedMembers.includes(member.id) 
                                                    ? 'bg-primary/10 dark:bg-golden/10 border border-primary/20 dark:border-golden/20' 
                                                    : 'hover:bg-gray-100 dark:hover:bg-gray-800 border border-transparent'
                                            }`}
                                        >
                                            <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center overflow-hidden shrink-0">
                                                {member.avatar_url ? (
                                                    <img src={member.avatar_url} alt={member.full_name} className="w-full h-full object-cover" />
                                                ) : (
                                                    <Users size={16} className="text-gray-500 dark:text-gray-400" />
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                                    {member.full_name}
                                                </p>
                                                {member.username && (
                                                    <p className="text-xs text-gray-500 truncate">@{member.username}</p>
                                                )}
                                            </div>
                                            <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                                                selectedMembers.includes(member.id)
                                                    ? 'border-primary bg-primary dark:border-golden dark:bg-golden'
                                                    : 'border-gray-300 dark:border-gray-600'
                                            }`}>
                                                {selectedMembers.includes(member.id) && (
                                                    <div className="w-2 h-2 bg-white rounded-full" />
                                                )}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </form>
                </div>

                <div className="p-4 border-t border-light-border dark:border-dark-border flex justify-end gap-3 bg-gray-50 dark:bg-gray-900">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        form="create-group-form"
                        disabled={creatingGroup || !name.trim()}
                        className="flex items-center gap-2 px-6 py-2 bg-primary dark:bg-golden text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50"
                    >
                        {creatingGroup && <Loader2 size={16} className="animate-spin" />}
                        Create Group
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CreateGroupModal;
